import type { PracticeData } from "@common/packets/practicePacket";
import { ZIndexes } from "@common/constants";
import { OPERATOR_DISABLED } from "@common/utils/operatorGeometry";
import { buildOperatorRig } from "@common/utils/operatorRig";
import { Graphics } from "pixi.js";
import { GameConsole } from "./console/gameConsole";
import { Game } from "./game";
import { Building } from "./objects/building";
import { CameraManager } from "./managers/cameraManager";
import { PracticeInput } from "./localPracticeInput";
import { SoundManager } from "./managers/soundManager";
import { InputManager } from "./managers/inputManager";
import { InputActions } from "@common/constants";
import { PIXI_SCALE } from "./utils/constants";
import { drawExtractionRing } from "./utils/extractionVisual";
import { UIManager } from "./managers/uiManager";
import { DevTestUI } from "./devTesting";
import "../scss/local-practice.scss";

function element<T extends HTMLElement>(selector: string): T {
    const result = document.querySelector<T>(selector);
    if (!result) throw new Error(`Missing practice UI element: ${selector}`);
    return result;
}

class LocalPractice {
    private graphics?: Graphics;
    private damageOverlay?: Graphics;
    private showDamageShape = false;
    private initialized = false;
    private balance = 0;
    private progress = 0;
    private collected?: number;
    private pickupAt = -Infinity;
    private coinVoiceEnds: number[] = [];
    private readonly balances = new Map<number, number>();
    private readonly extractions = new Map<number, number>();
    private readonly labels = new Map<number, HTMLDivElement>();
    private readonly coins = new Map<number, {
        x: number; y: number; z: number; fromX: number; fromY: number; fromZ: number;
        toX: number; toY: number; toZ: number; at: number; age: number; removedAt?: number;
    }>();

    start(): void {
        this.reset();
        if (this.initialized) return;
        this.initialized = true;
        const release = (): void => { PracticeInput.extracting = false; };
        const eligible = (): boolean => Game.gameStarted && !Game.gameOver && !Game.activePlayer?.dead;
        window.addEventListener("keydown", event => {
            if ((event.code === "F8" || event.key === "F8") && eligible() && !event.repeat) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.showDamageShape = !this.showDamageShape;
                return;
            }
            if (event.code !== "KeyX" || !eligible()) return;
            if (event.target instanceof HTMLElement && (event.target.matches("input,textarea") || event.target.isContentEditable)) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            PracticeInput.extracting = true;
        }, true);
        window.addEventListener("keyup", event => {
            if (event.code === "KeyX") release();
        }, true);
        window.addEventListener("blur", release);
        document.addEventListener("visibilitychange", release);
        const button = element<HTMLButtonElement>("#practice-extract");
        element<HTMLButtonElement>("#practice-dash").addEventListener("click", () => {
            if (eligible() && !PracticeInput.extracting) InputManager.addAction(InputActions.Dash);
            element<HTMLButtonElement>("#practice-dash").blur();
        });
        button.addEventListener("pointerdown", event => {
            if (!eligible()) return;
            event.stopPropagation();
            button.setPointerCapture(event.pointerId);
            PracticeInput.extracting = true;
        });
        button.addEventListener("pointerup", release);
        button.addEventListener("pointercancel", release);
        button.addEventListener("lostpointercapture", release);
    }

    update(data: PracticeData): void {
        DevTestUI.update(data.devTest);
        const hud = element("#practice-hud");
        hud.hidden = false;
        const dash = element<HTMLButtonElement>("#practice-dash");
        const cooldown = data.dashCooldownMs ?? 0;
        dash.disabled = cooldown > 0 || data.progress > 0;
        element("#practice-dash-status").textContent = cooldown > 0 ? `${(cooldown / 1000).toFixed(1)} s` : "Dash";
        element("#practice-dash-key").textContent = InputManager.binds.getInputsBoundToAction("dash").filter(Boolean)[0] ?? "Dash";
        dash.style.setProperty("--dash-ready", `${1 - cooldown / 10000}`);
        element("#gas-msg-info").textContent = `$${data.entry / 100} practice arena · ${data.bots} / 10 bots${data.devTest ? " · ADMIN TEST" : ""}${this.showDamageShape ? " · DAMAGE SHAPE (F8)" : ""}`;
        const balance = element("#practice-balance");
        balance.textContent = `$${(data.balance / 100).toFixed(2)}`;
        this.balances.clear();
        this.extractions.clear();
        for (const player of data.players) {
            this.balances.set(player.id, player.balance);
            if (player.extractionProgress) this.extractions.set(player.id, player.extractionProgress);
        }
        if (data.balance > this.balance && this.balance > 0) {
            balance.animate([{ transform: "scale(1.12)", color: "#ffe77b" }, { transform: "scale(1)", color: "#fff" }], { duration: 200 });
            const gain = element("#practice-gain");
            gain.textContent = `+${((data.balance - this.balance) / 100).toFixed(2)}`;
            gain.getAnimations().forEach(animation => animation.cancel());
            gain.animate([{ opacity: 1, transform: "translateY(0)" }, { opacity: 0, transform: "translateY(-25px)" }], { duration: 800 });
        }
        this.balance = data.balance;
        element("#momo-live-balance").textContent = `$${(data.balance / 100).toFixed(2)}`;
        element("#momo-live-kills").textContent = String(data.kills ?? 0);
        element("#momo-live-damage").textContent = String(data.damageDone ?? 0);
        element("#momo-live-taken").textContent = String(data.damageTaken ?? 0);
        const seconds = data.timeAlive ?? 0;
        element("#momo-live-time").textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
        const collected = data.collected ?? 0;
        const pickups = this.collected === undefined ? 0 : Math.max(0, collected - this.collected);
        this.collected = collected;
        if (pickups > 0 && !Game.activePlayer?.dead) {
            this.pickupAt = performance.now();
            // At most eight short voices, even when a whole hoard arrives in one snapshot.
            const soundNow = performance.now();
            this.coinVoiceEnds = this.coinVoiceEnds.filter(end => end > soundNow);
            const voices = SoundManager.has("momo_coin") ? Math.min(pickups, 8 - this.coinVoiceEnds.length) : 0;
            for (let i = 0; i < voices; i++) {
                // Expiry also recovers after stopped/muted sounds without pending timers.
                this.coinVoiceEnds.push(soundNow + 300);
                const sound = SoundManager.play("momo_coin", { speed: 1 + (i % 4) * 0.075 });
                sound.volume = 0.65 / Math.sqrt(voices);
                sound.update();
            }
        }
        element<HTMLProgressElement>("#practice-progress").value = data.progress;
        this.progress = data.progress;
        hud.style.setProperty("--extract-progress", `${data.progress}`);
        hud.classList.toggle("extracting", data.progress > 0);
        const extractSeconds = data.devTest?.fastExtract ? 2 : 8;
        const menuDuration = document.getElementById("momo-extraction-duration");
        if (menuDuration) menuDuration.textContent = String(extractSeconds);
        const extract = element<HTMLButtonElement>("#practice-extract");
        extract.title = `Hold X for ${extractSeconds} seconds. Damage interrupts extraction.`;
        extract.setAttribute("aria-label", `Hold X or hold this button for ${extractSeconds} seconds to extract`);
        dash.title = data.devTest?.unlimitedDash ? "Dash repeatedly with Shift. Walls still block movement." : "Dash with Shift. 10-second cooldown.";
        element("#practice-extract-label").textContent = data.progress > 0
            ? `Extracting ${(data.progress * extractSeconds).toFixed(1)} / ${extractSeconds} s`
            : "Hold to extract";

        const now = performance.now();
        const ids = new Set(data.coins.map(coin => coin.id));
        for (const [id, visual] of this.coins) {
            if (!ids.has(id)) visual.removedAt ??= now;
        }
        for (const coin of data.coins) {
            const visual = this.coins.get(coin.id);
            if (visual) {
                visual.fromX = visual.x; visual.fromY = visual.y; visual.fromZ = visual.z;
                visual.toX = coin.position.x; visual.toY = coin.position.y; visual.toZ = coin.height;
                visual.at = now; visual.age = coin.age; visual.removedAt = undefined;
            } else this.coins.set(coin.id, {
                x: coin.position.x, y: coin.position.y, z: coin.height,
                fromX: coin.position.x, fromY: coin.position.y, fromZ: coin.height,
                toX: coin.position.x, toY: coin.position.y, toZ: coin.height, at: now, age: coin.age
            });
        }
    }

    private renderPickup(graphics: Graphics, now: number): void {
        const pickupAge = (now - this.pickupAt) / 320;
        const collector = Game.activePlayer;
        if (pickupAge >= 0 && pickupAge < 1 && collector && !collector.dead) {
            const { x, y } = collector.container.position;
            const alpha = (1 - pickupAge) * 0.65;
            const radius = 32 + pickupAge * 38;
            graphics.circle(x, y, radius).stroke({ color: 0xffdf77, width: 3, alpha });
            for (let i = 0; i < 4; i++) {
                const angle = i * Math.PI / 2 + Math.PI / 4;
                const sx = x + Math.cos(angle) * radius, sy = y + Math.sin(angle) * radius;
                graphics.moveTo(sx - 4, sy).lineTo(sx + 4, sy)
                    .moveTo(sx, sy - 4).lineTo(sx, sy + 4).stroke({ color: 0xfff4c3, width: 2, alpha });
            }
        }
    }

    render(): void {
        if (Game.gameOver) return;
        this.graphics ??= new Graphics();
        const graphics = this.graphics;
        const container = CameraManager.getContainer(Game.layer);
        if (graphics.parent !== container) container.addChild(graphics);
        graphics.zIndex = ZIndexes.Loot;
        graphics.clear();
        const now = performance.now();
        this.renderPickup(graphics, now);
        if (this.showDamageShape) {
            this.damageOverlay ??= new Graphics();
            if (this.damageOverlay.parent !== container) container.addChild(this.damageOverlay);
            this.damageOverlay.zIndex = ZIndexes.Players + 0.2;
            this.damageOverlay.clear();
            for (const object of Game.objects) {
                if (!object.isPlayer || object.dead || !object.container.visible || object.layer !== Game.layer) continue;
                if (object.operatorPose === OPERATOR_DISABLED) continue;
                const scale = PIXI_SCALE * object.sizeMod;
                const x = object.container.position.x, y = object.container.position.y;
                for (const part of buildOperatorRig(object.operatorRigState)) {
                    for (const face of part.surfaces) this.damageOverlay.poly(face.points.flatMap(point => [x + point.x * scale, y + point.y * scale]));
                }
            }
            this.damageOverlay.fill({ color: 0x6effc4, alpha: 0.45 });
        } else this.damageOverlay?.clear();
        for (const [id, coin] of this.coins) {
            const t = Math.min(1, (now - coin.at) / 100);
            coin.x = coin.fromX + (coin.toX - coin.fromX) * t;
            coin.y = coin.fromY + (coin.toY - coin.fromY) * t;
            coin.z = coin.fromZ + (coin.toZ - coin.fromZ) * t;
            const fade = coin.removedAt === undefined ? 1 : Math.max(0, 1 - (now - coin.removedAt) / 160);
            if (!fade) { this.coins.delete(id); continue; }
            const x = coin.x * PIXI_SCALE, groundY = coin.y * PIXI_SCALE;
            const y = groundY - coin.z * PIXI_SCALE * 0.75 - (1 - fade) * 25;
            const airborne = coin.z > 0.1;
            const spin = airborne ? 0.25 + Math.abs(Math.cos((coin.age + now - coin.at) / 65 + id)) * 0.75 : 1;
            const width = 20 * spin * fade, height = 18 * fade;
            graphics.ellipse(x + 3, groundY + 12, 21 * fade, 8 * fade).fill({ color: 0x172218, alpha: 0.26 * fade });
            if (!airborne) graphics.circle(x, y, 27).fill({ color: 0xffc13c, alpha: 0.06 * fade });
            graphics.ellipse(x, y + 2, width, height).fill({ color: 0xb27722, alpha: fade }).stroke({ color: 0x63461d, width: 2, alpha: fade });
            graphics.ellipse(x, y - 1, width, height).fill({ color: 0xffd15a, alpha: fade });
            graphics.ellipse(x, y - 1, width * 0.66, height * 0.66).stroke({ color: 0xb27b2c, width: 2, alpha: fade });
            graphics.moveTo(x - width * 0.45, y - 11).lineTo(x + width * 0.3, y - 12).stroke({ color: 0xfff4b5, width: 3, alpha: fade });
            if (spin > 0.5) graphics.moveTo(x, y - 8).lineTo(x, y + 7).stroke({ color: 0xb27b2c, width: 3, alpha: fade });
        }
        for (const [id, label] of this.labels) {
            const player = Game.objects.get(id);
            if (!this.balances.has(id) || !player?.isPlayer || player.dead || player.destroyed) {
                label.remove();
                this.labels.delete(id);
            }
        }
        for (const [id, balance] of this.balances) {
            const player = Game.objects.get(id);
            if (!player?.isPlayer || player.dead || player.destroyed) continue;
            let label = this.labels.get(id);
            if (!label) {
                label = document.createElement("div");
                label.className = "player-nameplate";
                document.getElementById("player-nameplates")?.appendChild(label);
                this.labels.set(id, label);
            }
            const parent = player.layerContainer ?? CameraManager.getContainer(player.layer);
            // Follow the interpolated sprite, keeping the label upright as the body turns.
            const position = parent.toGlobal({ x: player.container.position.x, y: player.container.position.y - 70 * player.sizeMod });
            label.style.left = `${Math.round(position.x)}px`;
            label.style.top = `${Math.round(position.y)}px`;
            label.style.fontSize = `${Math.max(11, Math.min(16, 35 * CameraManager.container.scale.x))}px`;
            label.hidden = !player.container.visible || player.layer !== Game.layer || Building.coversPlayer(player)
                || GameConsole.getBuiltInCVar("cv_map_expanded") || !GameConsole.getBuiltInCVar("cv_draw_hud");
            label.style.opacity = String(player.container.alpha * parent.alpha);
            const name = UIManager.getRawPlayerName(id).replace(/[\r\n]/g, " ");
            const text = `${name}\n$${(balance / 100).toFixed(2)}`;
            if (label.textContent !== text) label.textContent = text;
            const extraction = this.extractions.get(id) ?? 0;
            if (extraction > 0 && player !== Game.activePlayer && player.layer === Game.layer
                && player.container.visible && player.container.alpha > 0 && !Building.coversPlayer(player)) {
                const { x, y } = player.container.position;
                drawExtractionRing(graphics, x, y, extraction, player.container.alpha);
            }
            if (player.dashVisualUntil > now && player.layer === Game.layer && player.container.visible) {
                const fade = Math.min(1, (player.dashVisualUntil - now) / 240) * player.container.alpha;
                const { x, y } = player.container.position;
                const dx = Math.cos(player.dashVisualAngle), dy = Math.sin(player.dashVisualAngle);
                // Three tapered ribbons and white cores in the existing batch, no new effect objects.
                for (const offset of [-30, 0, 30]) {
                    const tail = (offset === 0 ? 170 : 125) * (0.65 + fade * 0.35);
                    const headX = x - dx * 18 - dy * offset, headY = y - dy * 18 + dx * offset;
                    const halfWidth = offset === 0 ? 10 : 6;
                    graphics.poly([headX - dy * halfWidth, headY + dx * halfWidth,
                        x - dx * tail - dy * offset, y - dy * tail + dx * offset,
                        headX + dy * halfWidth, headY - dx * halfWidth]).fill({ color: 0x9fffd8, alpha: 0.42 * fade });
                    graphics.moveTo(headX, headY).lineTo(x - dx * tail * 0.82 - dy * offset, y - dy * tail * 0.82 + dx * offset)
                        .stroke({ color: 0xecfff7, alpha: 0.95 * fade, width: offset === 0 ? 5 : 3 });
                }
            }
        }
        const player = Game.activePlayer;
        if (this.progress > 0 && player && !player.dead && player.container.visible) {
            const { x, y } = player.container.position;
            drawExtractionRing(graphics, x, y, this.progress, player.container.alpha);
        }
    }

    reset(): void {
        DevTestUI.reset();
        PracticeInput.extracting = false;
        this.balance = 0;
        this.progress = 0;
        this.collected = undefined;
        this.coinVoiceEnds = [];
        this.pickupAt = -Infinity;
        this.coins.clear();
        this.balances.clear();
        this.extractions.clear();
        for (const label of this.labels.values()) label.remove();
        this.labels.clear();
        this.graphics?.destroy();
        this.graphics = undefined;
        this.damageOverlay?.destroy();
        this.damageOverlay = undefined;
        this.showDamageShape = false;
        const hud = document.querySelector<HTMLElement>("#practice-hud");
        if (hud) hud.hidden = true;
        const stats = document.getElementById("momo-match-stats");
        if (stats) stats.hidden = true;
        document.getElementById("btn-match-stats")?.setAttribute("aria-expanded", "false");
    }
}

export const LocalPracticeUI = new LocalPractice();
